import React, { useState } from 'react';
import './ProductFilter.css';

const ProductFilter = ({ filters, onFilterChange, categories, onReset }) => {
  const [isExpanded, setIsExpanded] = useState({
    category: true,
    price: true,
    brand: true,
    frameType: true,
    frameShape: true,
    frameMaterial: true,
    lensType: true,
    prescription: true,
    gender: true
  });

  const toggleSection = (section) => {
    setIsExpanded({ ...isExpanded, [section]: !isExpanded[section] });
  };

  // Price ranges
  const priceRanges = [
    { value: '', label: 'All Prices' },
    { value: '0-1000', label: 'Under ₹1,000' },
    { value: '1000-3000', label: '₹1,000 - ₹3,000' },
    { value: '3000-5000', label: '₹3,000 - ₹5,000' },
    { value: '5000-10000', label: '₹5,000 - ₹10,000' },
    { value: '10000-999999', label: 'Above ₹10,000' }
  ];

  // Brands (you can fetch these from API)
  const brands = ['Ray-Ban', 'Oakley', 'Acuvue', 'Gunnar', 'Prada', 'Gucci', 'Titan', 'Fastrack'];

  // Frame types
  const frameTypes = [
    { value: '', label: 'All Frame Types' },
    { value: 'full-rim', label: 'Full Rim' },
    { value: 'half-rim', label: 'Half Rim' },
    { value: 'rimless', label: 'Rimless' }
  ];

  // Frame shapes
  const frameShapes = [
    { value: '', label: 'All Shapes' },
    { value: 'aviator', label: 'Aviator' },
    { value: 'wayfarer', label: 'Wayfarer' },
    { value: 'round', label: 'Round' },
    { value: 'square', label: 'Square' },
    { value: 'rectangle', label: 'Rectangle' },
    { value: 'cat-eye', label: 'Cat Eye' }
  ];

  // Frame materials
  const frameMaterials = [
    { value: '', label: 'All Materials' },
    { value: 'metal', label: 'Metal' },
    { value: 'acetate', label: 'Acetate' },
    { value: 'plastic', label: 'Plastic' },
    { value: 'titanium', label: 'Titanium' },
    { value: 'stainless-steel', label: 'Stainless Steel' }
  ];

  // Lens types
  const lensTypes = [
    { value: '', label: 'All Lens Types' },
    { value: 'standard', label: 'Standard' },
    { value: 'blue-light-blocking', label: 'Blue Light Blocking' },
    { value: 'photochromic', label: 'Photochromic' },
    { value: 'polarized', label: 'Polarized' },
    { value: 'progressive', label: 'Progressive' },
    { value: 'bifocal', label: 'Bifocal' }
  ];

  // Prescription types
  const prescriptionTypes = [
    { value: '', label: 'All Prescriptions' },
    { value: 'single-vision', label: 'Single Vision' },
    { value: 'bifocal', label: 'Bifocal' },
    { value: 'progressive', label: 'Progressive' },
    { value: 'toric', label: 'Toric (Astigmatism)' },
    { value: 'non-prescription', label: 'Non-Prescription' }
  ];

  // Gender
  const genders = [
    { value: '', label: 'All' },
    { value: 'men', label: 'Men' },
    { value: 'women', label: 'Women' },
    { value: 'unisex', label: 'Unisex' }
  ];

  // Handle filter changes - send category_id instead of category
  const handleFilterChange = (filterName, value) => {
    if (filterName === 'category') {
      // Convert category to category_id for ProductPage
      onFilterChange({ category_id: value });
    } else {
      onFilterChange({ [filterName]: value });
    }
  };

  return (
    <div className="product-filter">
      <div className="filter-header">
        <h3>Filters</h3>
        <button onClick={onReset} className="reset-filters">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M3 12h18M3 18h18"/>
          </svg>
          Reset All
        </button>
      </div>

      {/* Category Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('category')}
        >
          <span>Category</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.category ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.category && (
          <div className="filter-options">
            <label className="filter-option">
              <input
                type="radio"
                name="category"
                value=""
                checked={filters.category_id === '' || !filters.category_id}
                onChange={() => handleFilterChange('category', '')}
              />
              <span>All Categories</span>
            </label>
            {categories && categories.map(cat => (
              <label key={cat.id} className="filter-option">
                <input
                  type="radio"
                  name="category"
                  value={cat.id}
                  checked={filters.category_id === String(cat.id)}
                  onChange={() => handleFilterChange('category', String(cat.id))}
                />
                <span>{cat.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Price Range Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('price')}
        >
          <span>Price Range</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.price ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.price && (
          <div className="filter-options">
            {priceRanges.map(range => (
              <label key={range.value} className="filter-option">
                <input
                  type="radio"
                  name="priceRange"
                  value={range.value}
                  checked={filters.priceRange === range.value}
                  onChange={() => onFilterChange({ priceRange: range.value })}
                />
                <span>{range.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Brand Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('brand')}
        >
          <span>Brand</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.brand ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.brand && (
          <div className="filter-options">
            <label className="filter-option">
              <input
                type="radio"
                name="brand"
                value=""
                checked={filters.brand === ''}
                onChange={() => onFilterChange({ brand: '' })}
              />
              <span>All Brands</span>
            </label>
            {brands.map(brand => (
              <label key={brand} className="filter-option">
                <input
                  type="radio"
                  name="brand"
                  value={brand}
                  checked={filters.brand === brand}
                  onChange={() => onFilterChange({ brand: brand })}
                />
                <span>{brand}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Frame Type Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('frameType')}
        >
          <span>Frame Type</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.frameType ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.frameType && (
          <div className="filter-options">
            {frameTypes.map(type => (
              <label key={type.value} className="filter-option">
                <input
                  type="radio"
                  name="frameType"
                  value={type.value}
                  checked={filters.frameType === type.value}
                  onChange={() => onFilterChange({ frameType: type.value })}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Frame Shape Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('frameShape')}
        >
          <span>Frame Shape</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.frameShape ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.frameShape && (
          <div className="filter-options">
            {frameShapes.map(shape => (
              <label key={shape.value} className="filter-option">
                <input
                  type="radio"
                  name="frameShape"
                  value={shape.value}
                  checked={filters.frameShape === shape.value}
                  onChange={() => onFilterChange({ frameShape: shape.value })}
                />
                <span>{shape.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Frame Material Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('frameMaterial')}
        >
          <span>Frame Material</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.frameMaterial ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.frameMaterial && (
          <div className="filter-options">
            {frameMaterials.map(material => (
              <label key={material.value} className="filter-option">
                <input
                  type="radio"
                  name="frameMaterial"
                  value={material.value}
                  checked={filters.frameMaterial === material.value}
                  onChange={() => onFilterChange({ frameMaterial: material.value })}
                />
                <span>{material.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Lens Type Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('lensType')}
        >
          <span>Lens Type</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.lensType ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.lensType && (
          <div className="filter-options">
            {lensTypes.map(type => (
              <label key={type.value} className="filter-option">
                <input
                  type="radio"
                  name="lensType"
                  value={type.value}
                  checked={filters.lensType === type.value}
                  onChange={() => onFilterChange({ lensType: type.value })}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Prescription Type Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('prescription')}
        >
          <span>Prescription Type</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.prescription ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.prescription && (
          <div className="filter-options">
            {prescriptionTypes.map(type => (
              <label key={type.value} className="filter-option">
                <input
                  type="radio"
                  name="prescriptionType"
                  value={type.value}
                  checked={filters.prescriptionType === type.value}
                  onChange={() => onFilterChange({ prescriptionType: type.value })}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Gender Filter */}
      <div className="filter-section">
        <div 
          className="filter-section-header"
          onClick={() => toggleSection('gender')}
        >
          <span>Gender</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points={isExpanded.gender ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </div>
        {isExpanded.gender && (
          <div className="filter-options">
            {genders.map(gender => (
              <label key={gender.value} className="filter-option">
                <input
                  type="radio"
                  name="gender"
                  value={gender.value}
                  checked={filters.gender === gender.value}
                  onChange={() => onFilterChange({ gender: gender.value })}
                />
                <span>{gender.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductFilter;